import React, { useState } from "react";
import { ICellRendererParams } from "ag-grid-community";
import { Popover } from "antd";
import { FaRegEdit } from "react-icons/fa";
import { TAG_COLORS } from "@/ts/constants";
import { GlobalTagRecordType, KwdColumns } from "@/ts/common-types";
import { titleCase } from "@/ts/utils";

const KeywordTagCellRenderer = (params: ICellRendererParams) => {
    const tags: string[] = params.value || [];
    const [tagHover, setTagHover] = useState(false);
    
    let colorIdx = 0;
    const field = params.colDef.field;

    if (field === KwdColumns.CATEGORY) colorIdx = 0;
    else if (field === KwdColumns.SUBCATEGORY) colorIdx = 1;
    else if (field === KwdColumns.BRAND) colorIdx = 2;
    
    const { fill, border, background } = TAG_COLORS[colorIdx];

    const commonTagStyle = {
        borderRadius: 8,
        width: 170,
        minWidth: 170,
        maxWidth: 170,
        height: 32,
        lineHeight: "32px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 10px 0 14px",
        letterSpacing: 0.15,
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        transition: "box-shadow 0.2s, border 0.2s, background 0.2s, color 0.2s",
        cursor: "pointer",
    };

    const handleEdit = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (params.colDef && params.api && params.node) {
            params.api.startEditingCell({
                rowIndex: params.node.rowIndex,
                colKey: params.colDef.field,
            });
        }
    };

    const popoverContent = () => (
        <div className="campaigns-tag-popover rounded shadow-sm" style={{ background: "#fff", padding: "8px" }}>
            {tags.map(tagName => (
                <div
                    key={tagName}
                    className="rounded d-flex align-items-center"
                    style={{
                        fontSize: "13px",
                        padding: "6px 12px",
                        marginBottom: "4px",
                        background: background,
                        color: fill
                    }}
                >
                    {titleCase(tagName)}
                </div>
            ))}
        </div>
    );

    return (
        <span
            className="mt-2"
            style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <span
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    minHeight: 32,
                    height: 32,
                }}
            >
                {tags.length > 0 ? (
                    <Popover
                        overlayClassName="custom-popover"
                        placement="right"
                        trigger="hover"
                        content={popoverContent}
                    >
                        <span
                            style={{
                                ...commonTagStyle,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                fontWeight: 500,
                                fontSize: 15,
                                color: fill,
                                verticalAlign: "middle",
                                background: tagHover
                                    ? "rgba(255,255,255,0.5)"
                                    : "rgba(255,255,255,0.35)",
                                boxShadow: tagHover
                                    ? "0 2px 8px 0 rgba(31, 38, 135, 0.12)"
                                    : "0 4px 24px 0 rgba(31, 38, 135, 0.08)",
                                border: `1.5px solid ${border}`,
                            }}
                            onMouseOver={() => setTagHover(true)}
                            onMouseOut={() => setTagHover(false)}
                            onClick={handleEdit}
                        >
                            <span
                                style={{
                                    width: "100%",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    textAlign: "left",
                                    flex: 1,
                                }}
                            >
                                {titleCase(tags[0])}
                                {tags.length > 1 && (
                                    <span style={{ marginLeft: 4, opacity: 0.7 }}>
                                        +{tags.length - 1}
                                    </span>
                                )}
                            </span>
                            <span
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    marginLeft: 8,
                                    padding: 3,
                                    borderRadius: 8,
                                    height: 26,
                                    width: 26,
                                    boxSizing: "border-box",
                                    background: "transparent",
                                    transition: "background 0.2s, box-shadow 0.2s",
                                }}
                            >
                                <FaRegEdit
                                    style={{
                                        color: tagHover ? fill : border,
                                        fontSize: 15,
                                        verticalAlign: "middle",
                                        transition: "color 0.2s",
                                    }}
                                />
                            </span>
                        </span>
                    </Popover>
                ) : (
                    <span
                        style={{
                            ...commonTagStyle,
                            fontStyle: "italic",
                            fontWeight: 400,
                            fontSize: 14,
                            color: tagHover ? fill : "#bbb",
                            background: tagHover
                                ? "rgba(255,255,255,0.5)"
                                : "rgba(255,255,255,0.25)",
                            boxShadow: tagHover
                                ? "0 2px 8px 0 rgba(31, 38, 135, 0.12)"
                                : "0 4px 24px 0 rgba(31, 38, 135, 0.08)",
                            border: `1.5px solid #e0e0e0`,
                        }}
                        onMouseOver={() => setTagHover(true)}
                        onMouseOut={() => setTagHover(false)}
                        onClick={handleEdit}
                    >
                        <span
                            style={{
                                width: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textAlign: "center",
                                flex: 1,
                            }}
                        >
                            {field === 'brand_list' ? '— Select One —' : '— Select Multiple —'}
                        </span>
                        <span
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginLeft: 8,
                                padding: 3,
                                borderRadius: 8,
                                height: 26,
                                width: 26,
                                boxSizing: "border-box",
                                background: "transparent",
                                transition: "background 0.2s, box-shadow 0.2s",
                            }}
                        >
                            <FaRegEdit
                                style={{
                                    color: tagHover ? fill : "#bbb",
                                    fontSize: 15,
                                    verticalAlign: "middle",
                                    transition: "color 0.2s",
                                }}
                            />
                        </span>
                    </span>
                )}
            </span>
        </span>
    );
};

export default KeywordTagCellRenderer;